import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../api/axiosInstance";
import { AxiosError } from "axios";
import { SERVICE_TYPE, PAYMENT_TYPE, ORDER_STATUS } from "../constants";
import * as consts from "../constants";

type User = {
  email: string;
  password: string;
};

type NewUser = User & {
  name: string;
};

type UserBasicInfo = {
  uid: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  addr1: string;
  addr2: string;
  addr3: string;
  addr4: string;
  addr5: string;
  prefer_driver: number;
  dislike_driver: number;
  is_guest: boolean;
  status: consts.customerStatus;
};

type DriverProfileData = {
  uid: string;
  name: string;
  phone: string;
  email: string;
  homeaddr: string;
  dayoff: number;
  next_days: number,
  prev_days: number,
};

export type AppConfigI18nData = {
  config_key: string;
  locale: string;
  value: string;
};

type OrderData = {
  customer_name: string;
  order_id?: number;
  customer_id: number;
  service_type: SERVICE_TYPE;
  phone?: string;
  line_id?: string;
  wechat_id?: string;
  addr1: string;
  addr1_city: string;
  addr1_state: string;
  addr2?: string;
  addr2_city?: string;
  addr2_state?: string;
  addr3?: string;
  addr3_city?: string;
  addr3_state?: string;
  airport: string;
  flight_number: string;
  /**
   * 接机时为接机时间
   * 送机时为送机时间
   */
  flight_time: Date;
  use_time: Date;
  base_fare: number;
  tips: number;
  base_fare_payment_method: PAYMENT_TYPE;
  tips_payment_method: PAYMENT_TYPE;
  notes?: string;
  passengers: number;
  luggages: number;
  carry_on: number;
  other?: string;
  language: string;
  contact_method?: string;
  vehicle_type: consts.vehicleType;
  qrcodeFile?: File;
}

type OrderStatus = {
  status: ORDER_STATUS;
  data: OrderData;
};

type ErrorResponse = {
  message: string;
};

export interface IOrderInfo {
  customer_name?: string;
  order_id: number;
  customer_id?: number;
  status: consts.orderStatus;
  service_type?: consts.serviceType;
  assigner?: number;
  driver?: number;
  driver_name?: string;
  driver_phone?: string,
  licence_plate?: string,
  avatar?: string,
  phone?: string;
  line_id?: string;
  wechat_id?: string;
  email?: string;
  addr1?: string;
  addr2?: string;
  addr3?: string;
  airport?: string;
  flight_number?: string;
  lobby_service?: boolean;
  /** 接机时为到达之间，送机时为航班离开时间 */
  flight_time?: Date;
  use_time?: Date;
  base_fare?: number;
  tips?: number;
  base_fare_payment_method?: consts.PAYMENT_TYPE;
  tips_payment_method?: consts.PAYMENT_TYPE;
  notes?: string;
  contact_method?: string;
  language?: string;
  passengers?: number;
  luggages?: number;
  carry_on?: number;
  other?: string;
  comment?: string;
  qrcode?: string;
  car_model?: string;
  seat?: number;
}

export interface IComment {
  comment_id?: number;
  desc?: string;
  customer_id?: number;
  driver_id?: number;
  customer_name?: string;
  driver_name?: string;
  order_id?: number;
  type?: consts.commentType;
  isClient?: boolean;
}

export interface IOplog {
  oplog_id?: number;
  desc?: string;
  field?: string;
  changes?: string;
  user_id?: number;
  order_id?: number;
  type?: consts.oplogType;
}

export interface IOrderNeedCanceled {
  order_id: number;
  user_id: number;
  desc: string;
  changes?: string;
}

type OrderPrice = {
  /**
   * 总费用
   */
  totalFee: number;
  /**
   * 基础费用
   */
  baseFee: number;
  /**
   * 接机费用
   */
  pickupFee: number;
  /**
   * 服务费用（进机场服务）
   */
  serviceFee: number;
  /**
   * 停车场费用
   */
  parkingFee: number;
  /**
   * 额外乘客费用
   */
  passengerFee: number;
  /**
   * 特殊时间段费用
   */
  specialTimeFee: number;
  /**
   * 额外停车点费用
   */
  stopsFee: number;
  /**
   * 其他费用
   */
  otherFee: number;
  /**
   * 优惠
   */
  promotion: number;
};

type AuthApiState = {
  basicUserInfo?: UserBasicInfo | null;
  driverInfo?: DriverProfileData | null;
  orderStatus?: OrderStatus | null;
  orders: IOrderInfo[];//any[];  // 或者使用具体的订单类型
  driver_orders: IOrderInfo[];
  orderPrice?: OrderPrice | null;
  status: "idle" | "loading" | "failed";
  error: string | null;
};

const initialState: AuthApiState = {
  // basicUserInfo: localStorage.getItem("userInfo")
  //   ? JSON.parse(localStorage.getItem("userInfo") as string)
  //   : null,
  // driverInfo: localStorage.getItem("driverInfo")
  // ? JSON.parse(localStorage.getItem("driverInfo") as string)
  // : null,
  basicUserInfo:
    localStorage.getItem("isDriver") !== "true"
      ? localStorage.getItem("userInfo")
        ? JSON.parse(localStorage.getItem("userInfo") as string)
        : null
      : null,
  driverInfo:
    localStorage.getItem("isDriver") === "true"
      ? localStorage.getItem("driverInfo")
        ? JSON.parse(localStorage.getItem("driverInfo") as string)
        : null
      : null,
  // basicUserInfo: null,
  // driverInfo: null,
  orderStatus: undefined,
  orders: [],
  driver_orders: [],
  orderPrice: undefined,
  status: "idle",
  error: null,
};

export const login = createAsyncThunk(
  "login",
  async (data: {type: consts.customerType, email?: string, password?: string, credential?: string, key?: string}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/customer/login2", data);
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      localStorage.setItem("userInfo", JSON.stringify(resData.userInfo));
      localStorage.setItem("token", resData.token);
      //localStorage.setItem('username', resData.userInfo.name);
      localStorage.removeItem("driverInfo");
      localStorage.removeItem("isDriver");

      return resData;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;

        return rejectWithValue(errorResponse);
      }

      throw error;
    }
  }
);

export const register = createAsyncThunk(
  "register",
  async (data: NewUser, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/customer/sign", data);
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      localStorage.setItem("userInfo", JSON.stringify(resData.userInfo));
      localStorage.setItem("token", resData.token);
      localStorage.removeItem("isDriver");

      return resData;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;

        return rejectWithValue(errorResponse);
      }

      throw error;
    }
  }
);

export const sendResetPasswordEmail = createAsyncThunk(
  "sendResetPasswordEmail",
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/customer/sendResetPasswordEmail", { email });
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return resData;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;

        return rejectWithValue(errorResponse);
      }

      throw error;
    }
  }
);

export const sendResetPasswordEmailToDriver = createAsyncThunk(
  "sendResetPasswordEmailToDriver",
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/user/sendDriverResetPasswordEmail", { email });
      const resData = response.data;
      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }
      return resData;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const resetPassword = createAsyncThunk(
  "resetPassword",
  async (data: {token: string, newPassword: string}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/customer/resetPassword", data);
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return resData;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;

        return rejectWithValue(errorResponse);
      }

      throw error;
    }
  }
);

export const changePassword = createAsyncThunk(
  "changePassword",
  async (
    data: { oldPassword: string; newPassword: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        "/customer/changePassword", 
        data,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return resData;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;

        return rejectWithValue(errorResponse);
      }

      throw error;
    }
  }
);

export const resetDriverPassword = createAsyncThunk(
  "resetDriverPassword",
  async (data: {token: string, newPassword: string}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/user/resetDriverPassword", data);
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return resData;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;

        return rejectWithValue(errorResponse);
      }

      throw error;
    }
  }
);

export const logout = createAsyncThunk(
  "logout",
  async (_, { rejectWithValue }) => {
    localStorage.removeItem("userInfo");
    localStorage.removeItem("driverInfo");
    localStorage.removeItem("token");
    localStorage.removeItem("dtoken");
    localStorage.removeItem("isDriver");
    return true;
    // try {
    //   const response = await axiosInstance.post("/logout", {});
    //   const resData = response.data;

    //   localStorage.removeItem("userInfo");

    //   return resData;
    // } catch (error) {
    //   if (error instanceof AxiosError && error.response) {
    //     const errorResponse = error.response.data;

    //     return rejectWithValue(errorResponse);
    //   }

    //   throw error;
    // }
  }
);

export const updateInfo = createAsyncThunk(
  "customer/updateCustomerById",
  async (userData: {
    customer_id: number;
    name?: string;
    phone?: string;
    addr1?: string;
    addr2?: string;
    company?: string;
    prefer_driver?: number;
    dislike_driver?: number;
  }, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.post(
        `/customer/updateCustomerById`,
        userData,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );

      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      // if (axios.isAxiosError(error)) {
      //   throw new Error(error.response?.data.message || "更新信息失败");
      // }
      // throw error;

      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;

        return rejectWithValue(errorResponse);
      }

      throw error;
    }
  }
);

export const removeCustomerAddr = createAsyncThunk(
  "customer/removeCustomerAddr",
  async (userData: {
    customer_id: number;
    addr: number;
  }, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.post(
        `/customer/removeCustomerAddr`,
        userData,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );

      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      // if (axios.isAxiosError(error)) {
      //   throw new Error(error.response?.data.message || "更新信息失败");
      // }
      // throw error;

      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;

        return rejectWithValue(errorResponse);
      }

      throw error;
    }
  }
);


export const updateDriverById = createAsyncThunk(
  "user/updateDriverById",
  async (userData: {
    user_id: number;
    phone?: string;
    homeaddr?: string;
    dayoff?: number;
    oldPassword?: string;
    newPassword?: string;
  }, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.post(
        `/user/updateDriverById`,
        userData,
        {
          headers: {
            Authorization: `${localStorage.getItem("dtoken")}`,
          },
        }
      );

      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;

        return rejectWithValue(errorResponse);
      }

      throw error;
    }
  }
);

export const placeOrder = createAsyncThunk(
  "order/createOrder",
  async (orderData: OrderData, {rejectWithValue}) => {
    try {
      const hasQrCodeFile = orderData.qrcodeFile instanceof File;
      let payload: OrderData | FormData = orderData;

      if (hasQrCodeFile) {
        const formData = new FormData();
        const { qrcodeFile, ...orderDataWithoutFile } = orderData as OrderData & {
          flightDate?: unknown;
          flightTime?: unknown;
          useDate?: unknown;
          useTime?: unknown;
          price_detail?: unknown;
          [key: string]: unknown;
        };

        // 前端临时字段不传给后端
        delete orderDataWithoutFile.flightDate;
        delete orderDataWithoutFile.flightTime;
        delete orderDataWithoutFile.useDate;
        delete orderDataWithoutFile.useTime;
        delete orderDataWithoutFile.price_detail;

        if (qrcodeFile instanceof File) {
          formData.append("qrcodeFile", qrcodeFile);
        }
        formData.append("orderData", JSON.stringify(orderDataWithoutFile));
        payload = formData;
      } else {
        const formData = new FormData();
        formData.append("orderData", JSON.stringify(orderData));
        payload = formData;
      }
      
      const response = await axiosInstance.post(
        '/order/createOrderWithQrcode',
        payload,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );

      const resData = response.data;
      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      // if (axios.isAxiosError(error)) {
      //   throw new Error(error.response?.data.message || "更新信息失败");
      // }
      // throw error;

      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;

        return rejectWithValue(errorResponse);
      }

      throw error;
    }
  }
);

// Add this type near the top with other type definitions
// type PriceErrorResponse = {
//   error: number;
//   message: string;
// };

export const getOrderPrice = createAsyncThunk(
  "getOrderPrice",
  async (orderData: OrderData, {rejectWithValue}) => {
    try {
      const orderPricePayload: OrderData = { ...orderData };
      delete orderPricePayload.qrcodeFile;

      const response = await axiosInstance.post(
        '/price/getOrderPrice',
        orderPricePayload,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );

      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
        // return rejectWithValue({
        //   error: resData.error,
        //   message: resData.errorDetail || resData.errorDetail.message
        // } as PriceErrorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;

        return rejectWithValue(errorResponse);
      }

      throw error;
    }
  }
);

export const driverLogin = createAsyncThunk(
  "driverLogin",
  async (data: User, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/user/driverLogin", data);
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      localStorage.setItem("driverInfo", JSON.stringify(resData.userInfo));
      localStorage.setItem("dtoken", resData.token);
      localStorage.setItem("isDriver", "true");
      localStorage.removeItem("userInfo");

      return resData;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;

        return rejectWithValue(errorResponse);
      }

      throw error;
    }
  }
);

export const getOrdersByDriverId = createAsyncThunk(
  "getOrdersByDriverId",
  async (params: { driver_id: number, date: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/order/getByDriverId',
        {
          driver_id: params.driver_id,
          date: params.date
        },
        {
          headers: {
            Authorization: `${localStorage.getItem("dtoken")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const getDriverOrdersByMonth = createAsyncThunk(
  "getDriverOrdersByMonth",
  async (params: { driver_id: number, month: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/order/getDriverOrdersByMonth',
        {
          driver_id: params.driver_id,
          month: params.month
        },
        {
          headers: {
            Authorization: `${localStorage.getItem("dtoken")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const getOrdersByCustomerId = createAsyncThunk(
  "getOrdersByCustomerId",
  async (params: { customer_id: number, count: number, needComments: boolean }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/order/getByCustomerId',
        params,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const getCustomerCurOrders = createAsyncThunk(
  "getCustomerCurOrders",
  async (params: { customer_id: number, count: number, needComments: false }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/order/getCustomerCurOrders',
        params,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const getFutureOrdersByCustomerId = createAsyncThunk(
  "getFutureOrdersByCustomerId",
  async (params: { customer_id: number }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/order/getFutureOrdersByCustomerId',
        {
          customer_id: params.customer_id,
          count: 30,
          needComments: false,
        },
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const changeOrderBasePaymentMethod = createAsyncThunk(
  "changeOrderBasePaymentMethod",
  async (
    params: {
      order_id: number;
      method: PAYMENT_TYPE;
      field: "base_fare_payment_method" | "tips_payment_method";
      user_id: number;
      desc: string;
      type: consts.oplogType.DRIVER_PROVIDED;
      changes: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        "/order/changeBasePaymentMethod",
        {
          order_id: params.order_id,
          method: params.method,
          field: params.field,
          user_id: params.user_id,
          desc: params.desc,
          type: params.type,
          changes: params.changes,
        },
        {
          headers: {
            Authorization: `${localStorage.getItem("dtoken")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse =
          resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const changeOrderTipsPaymentMethod = createAsyncThunk(
  "changeOrderTipsPaymentMethod",
  async (
    params: {
      order_id: number;
      method: PAYMENT_TYPE;
      field: "base_fare_payment_method" | "tips_payment_method";
      user_id: number;
      desc: string;
      type: consts.oplogType.DRIVER_PROVIDED;
      changes: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        "/order/changeTipsPaymentMethod",
        {
          order_id: params.order_id,
          method: params.method,
          field: params.field,
          user_id: params.user_id,
          desc: params.desc,
          type: params.type,
          changes: params.changes,
        },
        {
          headers: {
            Authorization: `${localStorage.getItem("dtoken")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse =
          resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const changeOrderStatus = createAsyncThunk(
  "changeOrderStatus",
  async (params: { order_id: number, status: consts.orderStatus, isClient?: boolean }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/order/changeOrderStatus',
        {
          order_id: params.order_id,
          status: params.status
        },
        {
          headers: {
            // Authorization: `${localStorage.getItem("dtoken")}`,
            Authorization: `${localStorage.getItem(params.isClient ? "token" : "dtoken")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const orderNeedCanceled = createAsyncThunk(
  "orderNeedCanceled",
  async (params: IOrderNeedCanceled, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/order/orderNeedCanceled",
        {
          order_id: params.order_id,
          user_id: params.user_id,
          desc: params.desc,
          changes: params.changes,
        },
        {
          headers: {
            Authorization: `${localStorage.getItem("dtoken")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const updateOrderPriceById = createAsyncThunk(
  "updateOrderPriceById",
  async (
    params: {
      order_id: number;
      base_fare?: number;
      tips?: number;
      user_id: number;
      desc: string;
      field: string;
      changes: string;
    },
    { rejectWithValue }
  ) => {
    try {
      let data: {
        order_id: number;
        base_fare?: number;
        tips?: number;
        user_id: number;
        desc: string;
        field: string;
        changes: string;
      } = {
        order_id: params.order_id,
        user_id: params.user_id,
        desc: params.desc,
        field: params.field,
        changes: params.changes,
      };
      if (params.base_fare) {
        data.base_fare = params.base_fare;
      }
      if (params.tips) {
        data.tips = params.tips;
      }
      const response = await axiosInstance.post(
        "/order/updateOrderPriceById",
        data,
        {
          headers: {
            Authorization: `${localStorage.getItem("dtoken")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse =
          resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const createComment = createAsyncThunk(
  "createComment",
  async (data: IComment, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/comment/create',
        data,
        {
          headers: {
            // Authorization: `${localStorage.getItem("dtoken")}`,
            Authorization: `${localStorage.getItem(data.isClient ? "token" : "dtoken")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const createOplog = createAsyncThunk(
  "createOplog",
  async (data: IOplog, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/oplog/create',
        data,
        {
          headers: {
            Authorization: `${localStorage.getItem("dtoken")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const searchOrderById = createAsyncThunk(
  "searchOrderById",
  async (params: { order_id: number, is_guest: boolean, orderToken?: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/order/searchGuestOrderById',
        {
          order_id: params.order_id,
          is_guest: params.is_guest,
        },
        {
          headers: {
            Authorization: `${params.orderToken || localStorage.getItem("token")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const getAppConfigI18nByKey = createAsyncThunk(
  "getAppConfigI18nByKey",
  async (
    params: { key: string; orderToken?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        "/app-config-i18n/getByKey",
        {
          key: params.key,
        },
        {
          headers: {
            Authorization: `${params.orderToken || localStorage.getItem("token")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data as AppConfigI18nData[];
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

export const updateMyOrderById = createAsyncThunk(
  "updateMyOrderById",
  async (orderData: OrderData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/order/updateMyOrderById',
        orderData,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );
      const resData = response.data;

      if (resData.error) {
        const errorResponse = resData.errorDetail || resData.errorDetail.message;
        return rejectWithValue(errorResponse);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);

const clearAuthenticatedState = (state: AuthApiState) => {
  state.basicUserInfo = null;
  state.driverInfo = null;
  state.orders = [];
  state.driver_orders = [];
  state.orderStatus = undefined;
  state.orderPrice = undefined;
  state.status = "idle";
  state.error = null;
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearSession: clearAuthenticatedState,
    resetOrderStatus: (state) => {
      state.orderStatus = {
        status: ORDER_STATUS.NONE,
        data: {} as OrderData
      };
    },
    updateDriverOrders: (state, action) => {
      state.driver_orders = action.payload;
      // console.log('orders in state:', state.driver_orders);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        login.fulfilled,
        //(state, action: PayloadAction<UserBasicInfo>) => {
        (state, action: PayloadAction<any>) => {
          state.status = "idle";
          state.basicUserInfo = action.payload.userInfo;
          // fix bug...,remove getUser
          // state.userProfileData = action.payload.userInfo;
          state.driverInfo = null;
        }
      )
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Login failed";
        } else {
          state.error = action.error.message || "Login failed";
        }
      })

      .addCase(register.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        register.fulfilled,
        (state, action: PayloadAction<UserBasicInfo>) => {
          state.status = "idle";
          // state.basicUserInfo = action.payload;
        }
      )
      .addCase(register.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Registration failed";
        } else {
          state.error = action.error.message || "Registration failed";
        }
      })

      .addCase(sendResetPasswordEmail.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        sendResetPasswordEmail.fulfilled,
        (state, action) => {
          state.status = "idle";
          // console.log('action', action);
          // console.log('33333');
        }
      )
      .addCase(sendResetPasswordEmail.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Send reset password mail failed";
        } else {
          state.error = action.error.message || "Send reset password mail failed";
        }
      })

      .addCase(resetPassword.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        resetPassword.fulfilled,
        (state, action) => {
          state.status = "idle";
        }
      )
      .addCase(resetPassword.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Send reset password mail failed";
        } else {
          state.error = action.error.message || "Send reset password mail failed";
        }
      })
      .addCase(logout.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(logout.fulfilled, (state, action) => {
        clearAuthenticatedState(state);
      })
      .addCase(logout.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Logout failed";
        } else {
          state.error = action.error.message || "Logout failed";
        }
      })

      .addCase(updateInfo.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateInfo.fulfilled, (state, action) => {
        state.status = "idle";
        //state.userProfileData = action.payload;
        state.basicUserInfo = action.payload;
        //state.user = action.payload;
      })
      .addCase(updateInfo.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Update my info failed";
      })

      .addCase(removeCustomerAddr.pending, (state) => {
        state.status = "loading";
      })
      .addCase(removeCustomerAddr.fulfilled, (state, action) => {
        state.status = "idle";
        state.basicUserInfo = action.payload;
      })
      .addCase(removeCustomerAddr.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Remove my addr failed";
      })
      
      .addCase(placeOrder.pending, (state) => {
        state.status = "loading";
      })
      .addCase(placeOrder.fulfilled, (state, action) => {
        state.status = "idle";
        state.orderStatus = { status:ORDER_STATUS.SUCCESS, data: action.payload };
      })
      .addCase(placeOrder.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Place order failed";
      })
      .addCase(updateMyOrderById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateMyOrderById.fulfilled, (state, action) => {
        state.status = "idle";
        state.orderStatus = { status:ORDER_STATUS.SUCCESS, data: action.payload };
      })
      .addCase(updateMyOrderById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Place order failed";
      })
      .addCase(getOrdersByCustomerId.pending, (state) => {
        state.orders = [];
        state.status = "loading";
      })
      .addCase(getOrdersByCustomerId.fulfilled, (state, action) => {
        state.status = "idle";
        state.orders = action.payload;
      })
      .addCase(getOrdersByCustomerId.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(getCustomerCurOrders.pending, (state) => {
        state.orders = [];
        state.status = "loading";
      })
      .addCase(getCustomerCurOrders.fulfilled, (state, action) => {
        state.status = "idle";
        state.orders = action.payload;
      })
      .addCase(getCustomerCurOrders.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(getFutureOrdersByCustomerId.pending, (state) => {
        state.orders = [];
        state.status = "loading";
      })
      .addCase(getFutureOrdersByCustomerId.fulfilled, (state, action) => {
        state.status = "idle";
        state.orders = action.payload;
      })
      .addCase(getFutureOrdersByCustomerId.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(driverLogin.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        driverLogin.fulfilled,
        //(state, action: PayloadAction<UserBasicInfo>) => {
        (state, action: PayloadAction<any>) => {
          state.status = "idle";
          state.driverInfo = action.payload.userInfo;
          state.basicUserInfo = null;
          // state.userProfileData = null;
        }
      )
      .addCase(driverLogin.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Login failed";
        } else {
          state.error = action.error.message || "Login failed";
        }
      })
      .addCase(getOrdersByDriverId.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.driver_orders = [];
      })
      .addCase(getOrdersByDriverId.fulfilled, (state, action) => {
        state.status = "idle";
        state.driver_orders = action.payload;
      })
      .addCase(getOrdersByDriverId.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Get driver orders failed";
        } else {
          state.error = action.error.message || "Get driver orders failed";
        }
      })
      
      .addCase(getDriverOrdersByMonth.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.driver_orders = [];
      })
      .addCase(getDriverOrdersByMonth.fulfilled, (state, action) => {
        state.status = "idle";
        state.driver_orders = action.payload;
      })
      .addCase(getDriverOrdersByMonth.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Get driver orders failed";
        } else {
          state.error = action.error.message || "Get driver orders failed";
        }
      })

      .addCase(searchOrderById.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.driver_orders = [];
      })
      .addCase(searchOrderById.fulfilled, (state, action) => {
        state.status = "idle";
        state.driver_orders = action.payload;
      })
      .addCase(searchOrderById.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Get order failed";
        } else {
          state.error = action.error.message || "Get order failed";
        }
      })
      .addCase(changeOrderBasePaymentMethod.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(changeOrderBasePaymentMethod.fulfilled, (state, action) => {
        state.status = "idle";
        // 更新订单列表中对应订单的支付方式
        state.driver_orders = state.driver_orders.map(order => 
          order.order_id === action.payload.order_id 
            ? { ...order, base_fare_payment_method: action.payload.base_fare_payment_method }
            : order
        );
      })
      .addCase(changeOrderBasePaymentMethod.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Change payment method failed";
        } else {
          state.error = action.error.message || "Change payment method failed";
        }
      })
      .addCase(changeOrderTipsPaymentMethod.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(changeOrderTipsPaymentMethod.fulfilled, (state, action) => {
        state.status = "idle";
        // 更新订单列表中对应订单的小费支付方式
        state.driver_orders = state.driver_orders.map(order => 
          order.order_id === action.payload.order_id 
            ? { ...order, tips_payment_method: action.payload.tips_payment_method }
            : order
        );
      })
      .addCase(changeOrderTipsPaymentMethod.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Change tips payment method failed";
        } else {
          state.error = action.error.message || "Change tips payment method failed";
        }
      })
      .addCase(changeOrderStatus.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(changeOrderStatus.fulfilled, (state, action) => {
        state.status = "idle";
        state.driver_orders = state.driver_orders.map(order => 
          order.order_id === action.payload.order_id 
            ? { ...order, status: action.payload.status }
            : order
        );
      })
      .addCase(changeOrderStatus.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Change order status failed";
        } else {
          state.error = action.error.message || "Change order status failed";
        }
      })
      .addCase(updateOrderPriceById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateOrderPriceById.fulfilled, (state, action) => {
        state.status = "idle";
        state.driver_orders = state.driver_orders.map(order => 
          order.order_id === action.payload.order_id 
            ? { ...order, base_fare: action.payload.base_fare, tips: action.payload.tips }
            : order
        );
      })
      .addCase(updateOrderPriceById.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Change order status failed";
        } else {
          state.error = action.error.message || "Change order status failed";
        }
      })

      .addCase(updateDriverById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateDriverById.fulfilled, (state, action) => {
        state.status = "idle";
        state.driverInfo = action.payload;
      })
      .addCase(updateDriverById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Update my info failed";
      })

      .addCase(createComment.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createComment.fulfilled, (state, action) => {
        state.status = "idle";
                // 更新对应订单的评论
        state.orders = state.orders.map(order => 
          order.order_id === action.payload.order_id 
            ? { ...order, comment: action.payload.desc }
            : order
          );
      })
      .addCase(createComment.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Create comment failed";
        } else {
          state.error = action.error.message || "Create comment failed";
        }
      })
      .addCase(createOplog.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createOplog.fulfilled, (state, action) => {
        state.status = "idle";
        // 更新对应订单的评论
        // state.orders = state.orders.map(order => 
        //   order.order_id === action.payload.order_id 
        //     ? { ...order, comment: action.payload.desc }
        //     : order
        //   );
      })
      .addCase(createOplog.rejected, (state, action) => {
        state.status = "failed";
        if (action.payload) {
          state.error =
            (action.payload as ErrorResponse).message || "Create oplog failed";
        } else {
          state.error = action.error.message || "Create oplog failed";
        }
      })
      .addCase(getOrderPrice.pending, (state) => {
        state.orderPrice = undefined;
        state.status = "loading";
      })
      .addCase(getOrderPrice.fulfilled, (state, action) => {
        state.status = "idle";
        state.orderPrice = action.payload;
      })
      .addCase(getOrderPrice.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Get order price failed";
        // state.orderPrice = null;
        // if (action.payload) {
        //   const payload = action.payload as PriceErrorResponse;
        //   state.error = payload.error === 11011 
        //     ? "NO_PRICE_INFO" 
        //     : payload.message || "Get order price failed";
        // } else {
        //   state.error = action.error.message || "Get order price failed";
        // }
      });
  }
});

export default authSlice.reducer;

// 导出新的 action
export const {
  clearSession,
  resetOrderStatus,
  updateDriverOrders,
} = authSlice.actions;
